/**
 * CounterSalesAggregate — R7a (2026-05-18).
 *
 * Aggregate counter sales by rep × month (per R7a-D1). "Unattributed"
 * appears as credited_rep_position_id IS NULL — same convention as
 * SalesByRep (R5). Role-gated to admin/manager/staff. admin/manager
 * see every rep × month row; staff are scoped to rows credited to a
 * rep position they're assigned to (per migration 014 — original 012
 * rejected staff entirely; fixed 2026-05-18 post-deploy hotfix).
 * Mirrors the SalesByRep shape with the month dimension added.
 *
 * Per 2026-05-18 operator clarification, counter sales scope is
 * subscription-CTA voucher confirmations only (counter_sales_v filters
 * to cta_type='subscription'). The "Total CTA Discount" column sums
 * the CTA's configured discount values; the actual subscription sale
 * dollar comes from Zuora webhook data and is a separate follow-up.
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getCounterSalesAggregate,
  type CounterSalesAggregateRow,
  type SalesActivityReportFilter,
} from "../../services/salesActivities.service";
import {
  resolvePositionNamesBulk,
  indexPositionsById,
} from "../../services/reportsSuite.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";

const PAGE_SIZE = 50;

const formatMonth = (iso?: string): string => {
  if (!iso) {
    return "";
  }
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
    });
  } catch {
    return iso;
  }
};

const formatCurrency = (amount: number | string | null): string => {
  if (amount === null || amount === undefined || amount === "") {
    return "";
  }
  // supabase-js may serialize NUMERIC columns as strings; coerce so
  // toLocaleString's currency options actually apply.
  const n = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(n)) {
    return "";
  }
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
};

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
): SalesActivityReportFilter => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  repPositionId: f.repPositionId,
  page,
  limit,
});

const CounterSalesAggregate: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [filters.dateFrom, filters.dateTo, filters.orgId, filters.repPositionId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-counter-sales-aggregate", filters, page],
    queryFn: () =>
      getCounterSalesAggregate(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const positionIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((r: any) => r.credited_rep_position_id)
            .filter((id: any): id is string => Boolean(id)),
        ),
      ),
    [rows],
  );

  const { data: positionRows } = useQuery({
    queryKey: ["counter-sales-aggregate-positions", positionIds],
    queryFn: () => resolvePositionNamesBulk(positionIds),
    enabled: positionIds.length > 0,
  });

  const positionsById = useMemo(
    () => indexPositionsById(positionRows ?? []),
    [positionRows],
  );

  const repDisplay = (positionId: string | null) => {
    if (!positionId) {
      return "Unattributed";
    }
    const p = positionsById.get(positionId);
    if (!p) {
      return positionId;
    }
    const label = p.assigned_name || p.title;
    return p.is_active ? label : `${label} (inactive)`;
  };

  const EXPORT_COLUMNS = [
    "Credited Rep",
    "Position ID",
    "Month",
    "Voucher Count",
    "Total CTA Discount",
    "Total Subscription Revenue",
    "Sales With Revenue",
  ];

  const mapRows = async (
    pageRows: CounterSalesAggregateRow[],
  ): Promise<Record<string, unknown>[]> => {
    const ids = Array.from(
      new Set(
        pageRows
          .map((r) => r.credited_rep_position_id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const positionRowsExport = await resolvePositionNamesBulk(ids);
    const exportPositionsById = indexPositionsById(positionRowsExport);
    const exportRepDisplay = (positionId: string | null) => {
      if (!positionId) {
        return "Unattributed";
      }
      const p = exportPositionsById.get(positionId);
      if (!p) {
        return positionId;
      }
      const label = p.assigned_name || p.title;
      return p.is_active ? label : `${label} (inactive)`;
    };
    return pageRows.map((r) => ({
      "Credited Rep": exportRepDisplay(r.credited_rep_position_id),
      "Position ID": r.credited_rep_position_id || "",
      Month: formatMonth(r.month),
      "Voucher Count": r.voucher_count,
      "Total CTA Discount": r.total_voucher_amount,
      "Total Subscription Revenue":
        r.total_subscription_revenue === null
          ? "Not available"
          : r.total_subscription_revenue,
      "Sales With Revenue": r.sales_with_revenue,
    }));
  };

  const fetchPage = async (f: ReportFilterState, p: number, l: number) => {
    const res = await getCounterSalesAggregate(filterToRpc(f, p, l));
    return { rows: res.rows, total: res.total };
  };

  return (
    <div className="space-y-4">
      <ReportPageHeader
        title="Counter Sales — Rep × Month"
        description="Subscription-CTA conversions aggregated by credited rep and month. Subscription Revenue sums members' actual Zuora subscription values (an n/total badge shows how many of the month's sales have synced revenue); 'Not available' until any sync. Total CTA Discount sums the CTA-configured discounts (kept for reference)."
        actions={
          <ExportButton
            filters={filters}
            fetchPage={fetchPage}
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="counter-sales-aggregate"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showState={false}
        showType={false}
        showRep={true}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Credited Rep</th>
              <th className="px-3 py-2">Month</th>
              <th className="px-3 py-2 text-right">Vouchers</th>
              <th className="px-3 py-2 text-right">Total CTA Discount</th>
              <th className="px-3 py-2 text-right">Subscription Revenue</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                  No counter sales match the current filters.
                </td>
              </tr>
            )}
            {rows.map((r: any, i: number) => (
              <tr
                key={`${r.credited_rep_position_id || "unattributed"}-${r.month}-${i}`}
                className={`hover:bg-gray-50 ${
                  !r.credited_rep_position_id ? "bg-yellow-50" : ""
                }`}
              >
                <td className="px-3 py-2 font-medium">
                  {repDisplay(r.credited_rep_position_id)}
                </td>
                <td className="px-3 py-2">{formatMonth(r.month)}</td>
                <td className="px-3 py-2 text-right">
                  {r.voucher_count.toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right">
                  {formatCurrency(r.total_voucher_amount)}
                </td>
                <td className="px-3 py-2 text-right">
                  {r.total_subscription_revenue === null ? (
                    <span className="text-gray-400">Not available</span>
                  ) : (
                    <>
                      {formatCurrency(r.total_subscription_revenue)}
                      {r.sales_with_revenue < r.voucher_count && (
                        <span className="ml-1 text-xs text-gray-400">
                          ({r.sales_with_revenue}/{r.voucher_count})
                        </span>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} rows`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default CounterSalesAggregate;
